import { useEffect, useState }  from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CardContent from '@mui/material/CardContent';
import { styled, useTheme } from '@mui/material/styles';
import MuiCard from '@mui/material/Card';
import InputAdornment from '@mui/material/InputAdornment';
import MuiFormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { useGoogleLogin } from '@react-oauth/google';
import googleicon from 'images/googleicon.png';
import { toast } from 'react-toastify';

import { GoogleLogin } from '@react-oauth/google';

// ** Custom Component Import
import CustomTextField from '../../../components/mui/text-field'
import { useMsal } from "@azure/msal-react";
import AppleIcon from '@mui/icons-material/Apple';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import AuthIllustrationV1Wrapper from '../login/AuthIllustrationV1Wrapper.js';
import BlankLayout from '../../Layout/blankLayout.js';

import useAuth from '../../../hooks/useAuth'; 
import { dev_baseUrl } from '../../../utils/var.js';

// ** Styled Components
const Card = styled(MuiCard)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: { width: '25rem' }
}))


// const FormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
//   '& .MuiFormControlLabel-label': {
//     color: theme.palette.text.secondary
//   }
// }))

const Login = () => {
  const [values, setValues] = useState({
    email: '',
    password: '',
    showPassword: false,
    rememberMe: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [message, setMessage] = useState(location.state?.message || '');
  const { login, logout } = useAuth();
  const { instance } = useMsal();
  // const [googleResponse, setGoogleResponse] = useState();
  const theme = useTheme();

  const user = {fullName:'', userName:'', teamName:'', roleName:'', jobTitle:'', crewLeader:''}; // Initialize user object with empty strings         

  // setOpenBackdrop(loading)

  // NOTE: We don't call logout() on mount anymore
  // In a multi-tab scenario, calling logout() here would clear credentials
  // that other tabs might still be using. The login form is already empty,
  // and users must enter credentials to log in anyway.

  useEffect(() => {
    // Check for any session expired flags (could be from any user)
    // We check all keys because we don't know which user was logged out
    let sessionExpiredFound = false;
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sessionExpired_')) {
        sessionExpiredFound = true;
        keysToRemove.push(key);
      }
    }
    
    if (sessionExpiredFound) {
      toast.error('Session expired', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
      
      // Remove all session expired flags
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Don't call logout() here - credentials might belong to another active tab
      // The user will need to log in again anyway
    }
  }, []);

  useEffect(() => {
    if (message || Object.keys(errors).length > 0) {
      const timer = setTimeout(() => {
        setMessage('');
        setErrors({});
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [message, errors]);

  const handleChange = prop => event => {
    setValues({ ...values, [prop]: event.target.value });
    setErrors({ ...errors, [prop]: '' });
  };

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };
  
  // const handleForgotPasswordClick = () => {
  //   navigate('/forgot-password');
  // };

  // const handleClickNew = () => {
  //   navigate('/register');
  // };

  const handleLogin = async (e) => {
    e.preventDefault();

    const { email, password } = values;
    const newErrors = {};

    if (!email) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (!password) {
      newErrors.password = 'Please enter a valid password.';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${dev_baseUrl}/Account/authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userName: email, password }),
      });

      const data = await response.json();
      console.log('Login response:', data);

      if (response.ok && !data.isError) {      

        login(data.result);

            // Check if the company is onboarded
        if (data.result.clientOnBoarding.isCompanyOnboarded) {
          navigate('/dashboard');
        } else {
          navigate('/welcome');
        }
      } else {
        setErrors({ form: data.message || 'Login failed' });
        console.log('Login error:', data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Error:', error);
      setErrors({ form: 'An error occurred. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    
    if (credentialResponse.credential) {
      
      const token = credentialResponse.credential;
      setLoading(true);

      try {
        const response = await fetch(`${dev_baseUrl}/Account/authenticate-sso`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ provider: 'Google', token }),
        });

        const data = await response.json();
        console.log('Google SSO response:', data);

        if (response.ok && !data.isError) {

          login(data.result);
          if (data.result.clientOnBoarding.isCompanyOnboarded) {
            navigate('/dashboard');
          } else {
            navigate('/welcome');
          }
        } else {
          setErrors({ form: data.message || 'Google SSO login failed' });
          console.log('Google SSO error:', data.message || 'Google SSO login failed');
        }
      } catch (error) {
        console.error('Error:', error);
        setErrors({ form: 'An error occurred with Google SSO. Please try again later.' });
      } finally {
        setLoading(false);
      }
    }
  };

  const loginWithGoogle = useGoogleLogin({
  onSuccess: handleGoogleLoginSuccess,
  onError: () => setErrors({ form: 'Google login failed' }),
  flow: 'implicit',
  });

  const handleHotmailLogin = async () => {
    const loginRequest = {
      scopes: ["openid", "profile", "User.Read"],
    };
    
    setLoading(true);    

    try {
      const loginResponse = await instance.loginPopup(loginRequest);

      console.log("Login Response:", loginResponse);

      const token = loginResponse.idToken;
      console.log("Token:", token);

      const response = await fetch(`${dev_baseUrl}/Account/authenticate-sso`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ provider: 'Microsoft', token }),
      });

      const data = await response.json();
      console.log('Hotmail SSO response:', data);

      if (response.ok && !data.isError) {

        login(data.result);
        if (data.result.clientOnBoarding.isCompanyOnboarded) {
          navigate('/dashboard');
        } else {
          navigate('/welcome');
        }
      } else {
        setErrors({ form: data.message || 'Hotmail SSO login failed' });
        console.log('Hotmail SSO error:', data.message || 'Hotmail SSO login failed');
      }
    } catch (error) {
      console.error('Error:', error);
      setErrors({ form: 'An error occurred with Hotmail SSO. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  const LinkStyled = styled(Link)(({ theme }) => ({
    textDecoration: 'underlined',
    color: `${theme.palette.primary.main} !important`
  }))

  const divider = (              
  <Divider
    sx={{
      color: 'text.disabled',
      '& .MuiDivider-wrapper': { px: 6 },
      fontSize: theme.typography.body2.fontSize,
      my: theme => `${theme.spacing(6)} !important`,
    }}
  >
    or
  </Divider>
  );

  return (
    <BlankLayout>
      <Box className="content-center">
        <AuthIllustrationV1Wrapper>
          <Card>
            <CardContent
              sx={{ p: (theme) => `${theme.spacing(10.5, 8, 8)} !important` }}
            >
              <Box
                sx={{
                  mb: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  height="55"
                  alt="error-illustration"
                  src="/images/Logo2.svg"
                />
              </Box>
              <Box sx={{ mb: 6, textAlign: "center" }}>
                <Typography variant="h4" sx={{ mb: 1.5 }}>
                  Login to your account
                </Typography>
              </Box>
              {message && (
                <Box mb={2} textAlign="center">
                  <Typography variant="h6" color="green">
                    {message}
                  </Typography>
                </Box>
              )}
              {errors.form && (
                <Box mb={2} textAlign="center">
                  <Typography variant="body1" color="error">
                    {errors.form}
                  </Typography>
                </Box>
              )}
              <form noValidate autoComplete="off" onSubmit={handleLogin}>
                <CustomTextField
                  autoFocus
                  fullWidth
                  id="email"
                  label="Email"
                  sx={{ mb: 4 }}
                  placeholder="example@gmail.com"
                  value={values.email}
                  onChange={handleChange("email")}
                  error={!!errors.email}
                  helperText={errors.email}
                  autoComplete="email"
                />
                <CustomTextField
                  fullWidth
                  sx={{ mb: 1.5 }}
                  label="Password"
                  value={values.password}
                  id="auth-login-password"
                  placeholder="············"
                  onChange={handleChange("password")}
                  type={values.showPassword ? "text" : "password"}
                  error={!!errors.password}
                  helperText={errors.password}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={handleClickShowPassword}
                          aria-label="toggle password visibility"
                        >
                          {values.showPassword ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box
                  sx={{
                    mb: 1.75,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="body2"
                    to="/forgot-password"
                    component={LinkStyled}
                    sx={{ my: 1.5 }}
                  >
                    Forgot Password?
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="main"
                  sx={{ mb: 4 }}
                  onClick={handleLogin}
                  disabled={loading}
                >
                  Login
                </Button>
              </form>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "text.secondary", mr: 2 }}>
                  Don't have an account?
                </Typography>
                <Typography
                  variant="body2"
                  component={LinkStyled}
                  to="/register"
                >
                  Create New
                </Typography>
              </Box>
              {loading ? null : divider}
              <Box
                sx={{
                  display: loading ? "none" : "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* <Box sx={{ mx: 2 }}>
                  <GoogleLogin
                    onSuccess={handleGoogleLoginSuccess}
                    onError={() => {
                      console.log("Login Failed");
                    }}
                    type="icon"
                    shape="circle"
                    disabled={loading}
                  />
                </Box> */}

                {/* <Box
                  sx={{
                    border: "1px solid #dadce0",
                    borderRadius: "20px",
                    mx: 2,
                  }}
                >


                  <IconButton
                    onClick={() => loginWithGoogle()}
                    sx={{
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                    disabled={loading}
                  >
                    <img
                      src={googleicon}
                      alt="google-logo"
                      style={{ width: 20, height: 20 }}
                    />
                  </IconButton>
                </Box> */}

                <GoogleLogin
                  onSuccess={handleGoogleLoginSuccess}
                  onError={() => setErrors({ form: "Google login failed" })}
                  theme="outline"
                  size="medium"                  
                  shape="pill"
                />

                {/* <Box sx={{border: '1px solid #dadce0', borderRadius: '20px', mx: 2}}>
                    <IconButton
                      href='/'
                      component={'a'}
                      sx={{ color: '#272727' }}
                      onClick={e => e.preventDefault()}
                      disabled={loading}
                    >
                      <AppleIcon />
                    </IconButton>
                  </Box> */}
                {/* <Box sx={{border: '1px solid #dadce0', borderRadius: '20px', mx: 2}}>
                    <IconButton
                      onClick={handleHotmailLogin}
                      sx={{ color: theme => (theme.palette.mode === 'light' ? '#272727' : 'grey.300') }}
                      disabled={loading}
                    >
                      <img src='/images/icon-outlook.png' alt='outlook-logo' />
                    </IconButton>
                  </Box> */}
              </Box>
            </CardContent>
          </Card>
        </AuthIllustrationV1Wrapper>
      </Box>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </BlankLayout>
  );
};

export default Login;